<app-side-menu>
  <div class="vehicle-rental-form-content">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="page-header">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <app-button 
                text="Voltar"
                color="var(--secondary-color)"
                (buttonClick)="cancel()"
                className="me-3">
                <i class="fas fa-arrow-left me-2"></i>
              </app-button>
              <div>
                <h1 class="page-title">
                  <i class="fas fa-plus-circle me-3"></i>
                  {{ isEditMode ? 'Editar' : 'Nova' }} Locação
                </h1>
              </div>
            </div>
            <div class="step-indicator">
              <span class="badge bg-primary">Passo {{ currentStep }} de {{ totalSteps }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <app-loading [show]="isLoading" message="Carregando dados da locação..."></app-loading>

    <!-- Form -->
    <div class="row" *ngIf="!isLoading">
      <div class="col-12">
        <form [formGroup]="rentalForm" (ngSubmit)="onSubmit()" class="rental-form-card">

          <!-- Step 1: Dados Básicos -->
          <div *ngIf="currentStep === 1" class="form-section">
            <h3 class="section-title">
              <i class="fas fa-info-circle me-2"></i>
              Dados Básicos
            </h3>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="rentalNumber" class="form-label">Número da Locação *</label>
                <input type="text" id="rentalNumber" class="form-control" formControlName="rentalNumber" readonly>
              </div>

              <div class="col-md-6">
                <label for="priority" class="form-label">Prioridade *</label>
                <select id="priority" class="form-select" formControlName="priority">
                  <option *ngFor="let priority of priorityOptions" [value]="priority.value">
                    {{ priority.label }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label for="status" class="form-label">Status *</label>
                <div *ngIf="loadingStatuses" class="loading-statuses">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  Carregando status...
                </div>
                <select
                  *ngIf="!loadingStatuses"
                  id="status"
                  class="form-select"
                  formControlName="status">
                  <option value="">Selecione um status</option>
                  <option *ngFor="let status of statusOptions" [value]="status.name">
                    {{ status.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label for="assignedStaffId" class="form-label">Funcionário Responsável *</label>
                <select id="assignedStaffId" class="form-select" formControlName="assignedStaffId" [disabled]="isLoadingStaff" [attr.aria-busy]="isLoadingStaff ? 'true' : null">
                  <option value="">Selecione um funcionário</option>
                  <option *ngFor="let s of staff" [value]="s.Id">
                    {{ s.FullName }}
                  </option>
                </select>
                <small class="text-muted" *ngIf="isLoadingStaff">Carregando funcionários...</small>
              </div>

              <div class="col-md-6">
                <label for="startDate" class="form-label">Data de Início *</label>
                <input type="date" id="startDate" class="form-control" formControlName="startDate" [min]="getTodayDate()">
              </div>

              <div class="col-md-6">
                <label for="endDate" class="form-label">Data de Fim *</label>
                <input type="date" id="endDate" class="form-control" formControlName="endDate">
              </div>

              <div class="col-12">
                <label for="notes" class="form-label">Observações</label>
                <textarea id="notes" class="form-control" formControlName="notes" rows="3"></textarea>
              </div>
            </div>
          </div>

          <!-- Step 2: Cliente -->
          <div *ngIf="currentStep === 2" class="form-section">
            <h3 class="section-title">
              <i class="fas fa-user me-2"></i>
              Cliente e Motorista
            </h3>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="customerId" class="form-label">Cliente *</label>
                <select id="customerId" class="form-select" formControlName="customerId" [disabled]="isLoadingCustomers" [attr.aria-busy]="isLoadingCustomers ? 'true' : null">
                  <option value="">Selecione um cliente</option>
                  <option *ngFor="let customer of customers" [value]="customer.id">
                    {{ customer.tradeName || customer.name }}
                  </option>
                </select>
                <small class="text-muted" *ngIf="isLoadingCustomers">Carregando clientes...</small>
              </div>

              <div class="col-md-6">
                <label for="driverName" class="form-label">Motorista</label>
                <input type="text" id="driverName" class="form-control" formControlName="driverName">
              </div>

              <div class="col-12" *ngIf="useCustomerAddress">
                <div class="alert alert-info">
                  <i class="fas fa-map-marker-alt me-2"></i>
                  Endereço será usado do cliente selecionado. Não é necessário informar endereço do motorista.
                </div>
              </div>

              <ng-container *ngIf="!useCustomerAddress">
                <div class="col-md-3">
                  <label for="driverZipCode" class="form-label">CEP</label>
                  <input type="text" id="driverZipCode" class="form-control" formControlName="driverZipCode" placeholder="00000-000" (input)="onDriverZipCodeInput($event)" [class.is-loading]="isLoadingDriverCep" [attr.aria-busy]="isLoadingDriverCep ? 'true' : null">
                  <div class="cep-status mt-1">
                    <div *ngIf="isLoadingDriverCep" class="d-flex align-items-center gap-2">
                      <span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span>
                      <small>Buscando endereço...</small>
                    </div>
                    <div *ngIf="!isLoadingDriverCep && driverCepError" class="text-danger">
                      <small>{{ driverCepError }}</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="driverStreet" class="form-label">Rua</label>
                  <input type="text" id="driverStreet" class="form-control" formControlName="driverStreet" [readonly]="isLoadingDriverCep">
                </div>

                <div class="col-md-3">
                  <label for="driverNumber" class="form-label">Número</label>
                  <input type="text" id="driverNumber" class="form-control" formControlName="driverNumber" [readonly]="isLoadingDriverCep">
                </div>

                <div class="col-md-3">
                  <label for="driverComplement" class="form-label">Complemento</label>
                  <input type="text" id="driverComplement" class="form-control" formControlName="driverComplement" [readonly]="isLoadingDriverCep">
                </div>

                <div class="col-md-4">
                  <label for="driverDistrict" class="form-label">Bairro</label>
                  <input type="text" id="driverDistrict" class="form-control" formControlName="driverDistrict" [readonly]="isLoadingDriverCep">
                </div>

                <div class="col-md-5">
                  <label for="driverCity" class="form-label">Cidade</label>
                  <input type="text" id="driverCity" class="form-control" formControlName="driverCity" [readonly]="isLoadingDriverCep">
                </div>

                <div class="col-md-3">
                  <label for="driverState" class="form-label">Estado</label>
                  <input type="text" id="driverState" class="form-control" formControlName="driverState" maxlength="2" placeholder="SP" [readonly]="isLoadingDriverCep">
                </div>
              </ng-container>
            </div>
          </div>

          <!-- Step 3: Veículos -->
          <div *ngIf="currentStep === 3" class="form-section">
            <h3 class="section-title">
              <i class="fas fa-car me-2"></i>
              Veículos
            </h3>

            <button type="button" class="btn btn-primary mb-3" (click)="addVehicle()">
              <i class="fas fa-plus me-2"></i>
              Adicionar Veículo
            </button>

            <div formArrayName="vehicles">
              <div *ngFor="let vehicle of vehiclesFormArray.controls; let i = index"
                   class="vehicle-item mb-3"
                   [formGroupName]="i">

                <div class="vehicle-header d-flex justify-content-between align-items-center">
                  <h5>Veículo {{ i + 1 }}</h5>
                  <button type="button" class="btn btn-outline-danger btn-sm" (click)="removeVehicle(i)">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Veículo *</label>
                    <select class="form-select" formControlName="vehicleId" [disabled]="isLoadingVehicles" [attr.aria-busy]="isLoadingVehicles ? 'true' : null">
                      <option value="">Selecione um veículo</option>
                      <option *ngFor="let v of vehicles" [value]="v.id">
                        {{ v.plateNumber }} - {{ v.brand }} {{ v.model }}
                      </option>
                    </select>
                    <small class="text-muted" *ngIf="isLoadingVehicles">Carregando veículos...</small>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Km Inicial *</label>
                    <input type="number" class="form-control" formControlName="startMileage" min="0">
                  </div>

                  <!-- Financial fields removed per new contract -->
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Anexos (apenas no modo de edição) -->
          <div *ngIf="currentStep === 4 && isEditMode" class="form-section">
            <h3 class="section-title">
              <i class="fas fa-paperclip me-2"></i>
              Anexos
            </h3>

            <div class="attachments-section">
              <!-- Upload de arquivos -->
              <div class="upload-section">
                <div class="upload-area" (click)="fileInput.click()">
                  <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
                  <h5>Arraste arquivos aqui ou clique para selecionar</h5>
                  <p class="text-muted">Formatos suportados: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF</p>
                  <input #fileInput type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" 
                         (change)="onFileSelected($event)" style="display: none;">
                </div>
              </div>

              <!-- Lista de arquivos selecionados -->
              <div *ngIf="selectedFiles.length > 0" class="selected-files mt-4">
                <h6><i class="fas fa-list me-2"></i>Arquivos Selecionados</h6>
                <div class="file-list">
                  <div *ngFor="let file of selectedFiles; let i = index" class="file-item">
                    <div class="file-info">
                      <i [class]="getFileIconClass(file)"></i>
                      <div class="file-details">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">{{ getFileSizeDisplay(file.size) }}</span>
                      </div>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-danger" (click)="removeFile(i)">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                
                <div class="upload-info mt-3">
                  <div class="alert alert-info" role="alert">
                    <i class="fas fa-info-circle me-2"></i>
                    <strong>Upload automático:</strong> Os arquivos serão enviados automaticamente ao avançar para o próximo passo.
                  </div>
                </div>
              </div>

              <!-- Lista de anexos existentes -->
              <div *ngIf="attachments.length > 0" class="existing-attachments mt-4">
                <h6><i class="fas fa-paperclip me-2"></i>Anexos Existentes</h6>
                <div class="attachment-list">
                  <div *ngFor="let attachment of attachments; let i = index" class="attachment-item">
                    <div class="attachment-info">
                      <i class="fas fa-file me-2"></i>
                      <span class="attachment-name">{{ attachment.fileName }}</span>
                      <span class="attachment-size">{{ getFileSizeDisplay(attachment.fileSize) }}</span>
                    </div>
                    <div class="attachment-actions">
                      <button type="button" class="btn btn-sm btn-outline-primary" 
                              (click)="downloadAttachment(attachment)"
                              title="Download">
                        <i class="fas fa-download"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" 
                              (click)="deleteAttachment(attachment, i)"
                              title="Excluir">
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4/5: Confirmação -->
          <div *ngIf="currentStep === (isEditMode ? 5 : 4)" class="form-section">
            <h3 class="section-title">
              <i class="fas fa-check-circle me-2"></i>
              Confirmação
            </h3>

            <div class="summary-card">
              <h5>Resumo da Locação</h5>
              <div class="summary-grid">
                <div class="summary-item">
                  <strong>Número:</strong>
                  <span>{{ rentalForm.get('rentalNumber')?.value }}</span>
                </div>
                <div class="summary-item">
                  <strong>Cliente:</strong>
                  <span>{{ (getSelectedCustomer()?.tradeName || getSelectedCustomer()?.name) || 'Não selecionado' }}</span>
                </div>
                <div class="summary-item">
                  <strong>Funcionário:</strong>
                  <span>{{ getSelectedStaff()?.FullName || 'Não selecionado' }}</span>
                </div>
                <div class="summary-item">
                  <strong>Veículos:</strong>
                  <span>{{ vehiclesFormArray.length }}</span>
                </div>
                <!-- Financial summary removed per new contract -->
                <div class="summary-item">
                  <strong>Status:</strong>
                  <span class="badge bg-warning">{{ rentalForm.get('status')?.value }}</span>
                </div>
              </div>
            </div>

            <div class="alert alert-info mt-3" role="alert">
              <i class="fas fa-info-circle me-2"></i>
              <strong>Próximos passos:</strong> Após criar a locação, você poderá adicionar anexos e documentos na tela de detalhes.
            </div>
          </div>

          <!-- Navigation -->
          <div class="form-navigation mt-4">
            <div class="d-flex gap-2">
              <app-button 
                text="Voltar"
                color="var(--secondary-color)"
                (buttonClick)="previousStep()"
                [disabled]="currentStep === 1">
                <i class="fas fa-arrow-left me-2"></i>
              </app-button>

              <app-button 
                text="Cancelar"
                color="#dc3545"
                (buttonClick)="cancel()">
                <i class="fas fa-times me-2"></i>
              </app-button>
            </div>

            <span class="step-info">Passo {{ currentStep }} de {{ totalSteps }}</span>

            <div>
              <app-button *ngIf="currentStep < totalSteps"
                text="Próximo"
                color="var(--primary-color)"
                (buttonClick)="nextStep()"
                [disabled]="!canGoNext() || isLoadingDriverCep">
                <i class="fas fa-arrow-right ms-2"></i>
              </app-button>

              <app-button *ngIf="currentStep === totalSteps"
                [text]="isSaving ? 'Salvando...' : 'Salvar Locação'"
                color="#198754"
                type="submit"
                [disabled]="isSaving || isLoadingDriverCep">
                <i class="fas fa-save me-2" *ngIf="!isSaving"></i>
                <span class="spinner-border spinner-border-sm me-2" *ngIf="isSaving"></span>
              </app-button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</app-side-menu>
